
import { Component, Vue, Prop } from 'vue-property-decorator';
import { SupplierModel } from '@/models/supplier';
import { BuyingListModel } from '@/models/buying_list';
import { BuyingLists } from '@/collections/buying_lists';
import { StoreLocationList } from '@/collections/store_locations';
import { StoreLocationModel } from '@/models/store_location';
import { CurrencyHelper } from '@/helpers/CurrencyHelper';
import FeatureLockedNotify from '@/components/FeatureLockedNotify.vue';
import { StoreModel } from '@/models/store';
import ConvertToPODialog from '@/components/purchase-order/ConvertToPODialog.vue';
import CopyLinkButton from '@/components/CopyLinkButton.vue';
import { EventBus } from "@/main";

// --- Moved Type Definitions Here ---
// Define interface for Buying List Item
interface IBuyingListItem {
  id: number;
  variantId: number;
  productName: string;
  sku: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  _action?: 'update' | 'remove';
}

// Define interface for item changes map
interface IItemChange {
  quantity?: number; // Make quantity optional
  unitPrice?: number; // Add optional unitPrice
  sku?: string; // Add optional sku
  action: 'update' | 'remove';
}
// --- End Moved Type Definitions ---

@Component({
  components: {
    FeatureLockedNotify,
    ConvertToPODialog,
    CopyLinkButton,
  }
})
export default class BuyingListDetailView extends Vue {
  private CurrencyHelper = CurrencyHelper;
  private buyingList: BuyingListModel | null = null;
  private supplier: SupplierModel | null = null;
  private search = '';
  private saving = false;
  private isEditing = false;
  private itemChanges: Map<number, IItemChange> = new Map();
  private originalItems: IBuyingListItem[] = [];
  private showCancelDialog = false;
  private showEmailDialog = false;
  private emailSubject = '';
  private emailMessage = '';
  private isEmailFormValid = false;
  private sending = false;
  private attachmentFormat: 'pdf' | 'csv' = 'pdf';
  private emailRecipients: string[] = [];
  private storeLocationList = new StoreLocationList();
  private locations: StoreLocationModel[] = [];
  private loadingLocations = false;
  private finalizeLoading = false;
  private cancelLoading = false;
  private completeLoading = false;
  private showFinalizeDialog = false;
  private showCompleteDialog = false;
  private showConvertDialog = false;

  @Prop({ default: () => new StoreModel() })
  private store!: StoreModel;
  
  // Computed property for dynamic headers
  get headers() {
    const baseHeaders = [
      { text: 'Variant ID', value: 'variantId' },
      { text: 'Name', value: 'productName' },
      { text: 'SKU', value: 'sku' },
      { text: 'Quantity', value: 'quantity', width: '120px' },
      { 
        text: 'Unit Cost', 
        value: 'unitPrice', 
        width: '130px',
      },
      { 
        text: 'Total', 
        value: 'totalPrice', 
        width: '120px',
      }
    ];

    if (this.isEditing) {
      baseHeaders.push({
        text: 'Actions',
        value: 'actions',
        width: '100px'
      });
    }

    return baseHeaders;
  }

  async created() {
    this.fetchLocations();
    await this.fetchData();
  }

  private async fetchData() {
    try {
      const { id } = this.$route.params;
      const buyingLists = new BuyingLists();
      this.buyingList = await buyingLists.getOne(id);

      if (this.buyingList?.supplierId) {
        this.supplier = new SupplierModel({ id: this.buyingList.supplierId });
        await this.supplier.getSupplierDetails();
      }

      // Store original items for comparison
      this.originalItems = JSON.parse(JSON.stringify(this.buyingList?.items || []));
      // Ensure totalPrice and totalValue are calculated initially
      this.recalculateItemTotals();
      this.recalculateTotalValue();
    } catch (error) {
      EventBus.$emit("show-snackbar", { message: `Failed to load buying list details`, color: "error" });
    }
  }

  private async fetchLocations() {
    this.loadingLocations = true;
    try {
      await this.storeLocationList.fetch();
      this.locations = this.storeLocationList.items;
    } catch (error) {
      EventBus.$emit("show-snackbar", { message: `Failed to load store locations`, color: "error" });
    } finally {
      this.loadingLocations = false;
    }
  }

  private formatNumber(value: number | undefined): string {
    if (!value) return '0';
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  }

  private getStatusColor(status: string | undefined): string {
    if (!status) return 'grey';
    
    const statusColors: { [key: string]: string } = {
      draft: 'grey',
      sent: 'blue',
      confirmed: 'purple',
      partial_received: 'orange',
      received: 'green',
      canceled: 'red',
      finalized: 'teal',
      active: 'orange'
    };

    return statusColors[status] || 'grey';
  }

  private getStockLevelColor(item: any): string {
    const stock = item[`[Actual Inventory Quantity]`];
    if (stock <= 0) return 'error';
    if (stock <= 10) return 'warning';
    return 'success';
  }

  get getOrderValueStatus(): string {
    if (!this.supplier || !this.buyingList) return 'grey';
    return this.isValidTotalValue ? 'success' : 'error';
  }

  get getOrderValueMessage(): string {
    if (!this.supplier || !this.buyingList) return 'Invalid';
    const totalValue = parseFloat(this.buyingList.totalValue.toString());
    const minOrderValue = parseFloat(this.supplier.minOrderValue.toString());
    const maxOrderValue = parseFloat(this.supplier.maxOrderValue.toString());

    if (totalValue < minOrderValue) {
      return `Below Minimum (${CurrencyHelper.formatCurrency(minOrderValue)})`;
    }
    if (totalValue > maxOrderValue) {
      return `Exceeds Maximum (${CurrencyHelper.formatCurrency(maxOrderValue)})`;
    }
    return 'Valid Order Value';
  }

  get getOrderValueClass(): string {
    return this.getOrderValueStatus === 'success' ? 'success--text' : 'error--text';
  }

  get isEditable(): boolean {
    return this.buyingList?.status === 'active';
  }

  get isValidForFinalize(): boolean {
    return !!(
      this.buyingList &&
      this.buyingList.status === 'active' &&
      this.isValidTotalValue
    );
  }

  get isValidTotalValue(): boolean {
    if (!this.supplier || !this.buyingList) return false;
    const totalValue = parseFloat(this.buyingList.totalValue.toString());
    const minOrderValue = parseFloat(this.supplier.minOrderValue.toString());
    const maxOrderValue = parseFloat(this.supplier.maxOrderValue.toString());
    return totalValue >= minOrderValue && totalValue <= maxOrderValue;
  }

  get isValidForComplete(): boolean {
    return !!(
      this.buyingList &&
      this.buyingList.status === 'finalized' &&
      this.buyingList.items &&
      this.buyingList.items.length > 0
    );
  }

  get locationOptions() {
    return this.locations.map(location => ({
      id: location.id,
      name: location.name
    }));
  }

  private async handleLocationChange(locationId: number) {
    if (!this.buyingList) return;
    
    try {
      // Save the buying list with new location
      await this.buyingList.save();
      EventBus.$emit("show-snackbar", { message: 'Store location updated successfully', color: "success" });
    } catch (error) {
      EventBus.$emit("show-snackbar", { message: `Failed to update store location`, color: "error" });
    }
  }

  private async toggleEditMode() {
    if (this.isEditing) {
      await this.saveBuyingList();
      this.isEditing = false;
    } else {
      this.isEditing = true;
    }
  }

  private async updateItemQuantity(item: IBuyingListItem) {
    if (!this.buyingList || !this.isEditing || !this.isEditable) return;
    
    // Ensure quantity and unitPrice are numbers and calculate total
    const quantity = Number(item.quantity || 0);
    const unitPrice = Number(item.unitPrice || 0);
    item.totalPrice = quantity * unitPrice;

    // Get existing changes or initialize
    const currentChange = this.itemChanges.get(item.id) || { action: 'update' };
    
    this.itemChanges.set(item.id, {
      ...currentChange,
      quantity: item.quantity,
      action: 'update' // Ensure action is update
    });
    
    // Recalculate total list value
    this.recalculateTotalValue();
  }

  // New method to handle unit price changes
  private updateItemUnitPrice(item: IBuyingListItem) {
    if (!this.buyingList || !this.isEditing || !this.isEditable) return;

    // Ensure quantity and unitPrice are numbers and calculate total
    const quantity = Number(item.quantity || 0);
    const unitPrice = Number(item.unitPrice || 0);
    item.totalPrice = quantity * unitPrice;

    // Get existing changes or initialize
    const currentChange = this.itemChanges.get(item.id) || { action: 'update' };

    this.itemChanges.set(item.id, {
      ...currentChange,
      unitPrice: item.unitPrice,
      action: 'update' // Ensure action is update
    });

    // Recalculate total list value
    this.recalculateTotalValue();
  }

  // New method to track SKU changes
  private updateItemSku(item: IBuyingListItem) {
    if (!this.buyingList || !this.isEditing || !this.isEditable) return;

    // Get existing changes or initialize
    const currentChange = this.itemChanges.get(item.id) || { action: 'update' };

    this.itemChanges.set(item.id, {
      ...currentChange,
      sku: item.sku, // Track sku change specifically if needed by backend, otherwise direct item modification might suffice
      action: 'update' // Ensure action is update
    });
    
    // Note: SKU change doesn't directly impact total value, so no recalculation needed here.
  }

  private removeItem(item: IBuyingListItem) {
    if (!this.buyingList || !this.isEditing || !this.isEditable) return;
    
    // Get existing change if any, but force action to 'remove'
    const currentChange = this.itemChanges.get(item.id) || {}; 
    this.itemChanges.set(item.id, {
      ...currentChange,
      quantity: 0, // Set quantity to 0 for remove context if needed by save logic
      unitPrice: item.unitPrice, // Keep unit price context if needed by save logic
      action: 'remove' 
    });

    const index = this.buyingList.items.findIndex(i => i.id === item.id);
    if (index !== -1) {
      this.buyingList.items.splice(index, 1);
    }
    
    // Recalculate total list value
    this.recalculateTotalValue();
  }

  private async saveItemChanges() {
    if (!this.buyingList || !this.itemChanges.size) return;

    try {
      const changes = Array.from(this.itemChanges.entries()).map(([id, change]) => {
        const originalItem = this.originalItems.find(i => i.id === id);
        const currentItem = this.buyingList!.items.find(i => i.id === id); // Might be undefined if removed

        // For updates, get current state; for removals, use original state (or just ID)
        const itemState = change.action === 'remove' ? originalItem : currentItem;

        if (!itemState && change.action !== 'remove') {
          console.warn(`Could not find item with id ${id} for change.`);
          return null; // Skip this change if item not found
        }
        
        const payload: any = {
          id,
          _action: change.action
        };

        // Add item details for updates
        if (change.action === 'update' && itemState) {
          payload.variantId = itemState.variantId;
          payload.productName = itemState.productName;
          payload.sku = itemState.sku;
          // Use the potentially updated quantity and unitPrice from the change map or current item state
          payload.quantity = Number(change.quantity !== undefined ? change.quantity : itemState.quantity || 0);
          payload.unitPrice = Number(change.unitPrice !== undefined ? change.unitPrice : itemState.unitPrice || 0);
          // Make sure to use the current sku from the item if it was edited directly
          payload.sku = itemState.sku; 
          // Recalculate totalPrice based on the final quantity and unitPrice being sent
          payload.totalPrice = payload.quantity * payload.unitPrice;
        }

        return payload;
      }).filter(change => change !== null); // Remove null entries if any items weren't found

      if (changes.length > 0) {
        // Save changes in batch
        this.buyingList.setItemChanges(changes);
        await this.buyingList.saveItemChanges();
      }
      
      // Clear changes after successful save
      this.itemChanges.clear();
      // Update original items to reflect saved state
      this.originalItems = JSON.parse(JSON.stringify(this.buyingList.items));
      // Recalculate total value based on potentially updated list from API response (or local data if API doesn't return it)
      this.recalculateItemTotals(); // Ensure individual item totals are correct
      this.recalculateTotalValue(); 
      
      EventBus.$emit("show-snackbar", { message: 'Item changes saved successfully', color: "success" });
    } catch (error) {
      EventBus.$emit("show-snackbar", { message: `Failed to save item changes`, color: "error" });
      // Revert to original items on error
      if (this.buyingList) {
        this.buyingList.items = JSON.parse(JSON.stringify(this.originalItems));
        this.recalculateItemTotals(); // Recalculate totals based on reverted items
        this.recalculateTotalValue(); 
      }
      // Clear failed changes
      this.itemChanges.clear();
      // Rethrow or handle as needed
      throw error; // Propagate error to saveBuyingList
    }
  }

  private async saveBuyingList() {
    if (!this.buyingList) return;
    
    this.saving = true;
    try {
      await this.saveItemChanges(); // This will throw if it fails
      
      // Recalculate total value before saving the main list object
      this.recalculateTotalValue(); 
      
      // Then save the buying list itself with updated fields (name, description, location, totalValue)
      await this.buyingList.save();
      EventBus.$emit("show-snackbar", { message: 'Buying list saved successfully', color: "success" });
      this.isEditing = false;
    } catch (error) {
      EventBus.$emit("show-snackbar", { message: `Failed to save buying list`, color: "error" });
    } finally {
      this.saving = false;
    }
  }

  private async finalizeBuyingList() {
    if (!this.buyingList || this.buyingList.status !== 'active') return;

    // Check if there are any unsaved changes
    if (this.itemChanges.size > 0) {
      try {
        await this.saveBuyingList();
      } catch (error) {
        EventBus.$emit("show-snackbar", { message: `Please save all changes before finalizing`, color: "error" });
        return;
      }
    }

    // Validate order value
    if (!this.isValidTotalValue) {
      // Replace emit with EventBus
      EventBus.$emit("show-snackbar", {
        message: `Order value must be within supplier limits before finalizing`,
        color: "error"
      });
      return;
    }
    
    // Show dialog instead of immediate action
    this.showFinalizeDialog = true;
  }

  private async confirmFinalize() {
    if (!this.buyingList) return;
    
    this.finalizeLoading = true;
    try {
      await this.buyingList.updateStatus('finalized');
      this.isEditing = false; // Exit edit mode if active
      this.showFinalizeDialog = false;
      EventBus.$emit("show-snackbar", { message: 'Buying list finalized successfully', color: "success" });
    } catch (error) {
      EventBus.$emit("show-snackbar", { message: `Failed to finalize buying list`, color: "error" });
    } finally {
      this.finalizeLoading = false;
    }
  }

  private async completeBuyingList() {
    if (!this.buyingList || this.buyingList.status !== 'finalized') return;
    
    // Show dialog instead of immediate action
    this.showCompleteDialog = true;
  }

  private async confirmComplete() {
    if (!this.buyingList) return;
    
    this.completeLoading = true;
    try {
      await this.buyingList.updateStatus('completed');
      this.showCompleteDialog = false;
      EventBus.$emit("show-snackbar", { message: 'Buying list completed successfully', color: "success" });
    } catch (error) {
      EventBus.$emit("show-snackbar", { message: `Failed to complete buying list`, color: "error" });
    } finally {
      this.completeLoading = false;
    }
  }

  private async cancelBuyingList() {
    if (!this.buyingList || this.buyingList.status === 'canceled') return;
    this.showCancelDialog = true;
  }

  private async confirmCancel() {
    if (!this.buyingList) return;
    
    this.cancelLoading = true;
    try {
      await this.buyingList.cancelList();
      this.isEditing = false; // Exit edit mode if active
      this.showCancelDialog = false;
      EventBus.$emit("show-snackbar", { message: 'Buying list canceled successfully', color: "success" });
      
      // Navigate back to the buying lists overview page
      this.$router.push('/buying-lists');
    } catch (error) {
      EventBus.$emit("show-snackbar", { message: `Failed to cancel buying list`, color: "error" });
    } finally {
      this.cancelLoading = false;
    }
  }

  private validateEmail(email: string): boolean {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  }

  private removeRecipient(email: string) {
    const index = this.emailRecipients.indexOf(email);
    if (index >= 0) {
      this.emailRecipients.splice(index, 1);
    }
  }

  private async sendBuyingList() {
    if (!this.buyingList || !this.supplier) return;
    
    // Initialize default values
    const totalValue = Number(this.buyingList.totalValue || 0);
    const merchantName = this.$store.state.merchant?.name || 'Merchant';
    
    this.emailSubject = `New Buying List from ${this.supplier.name} - ${this.buyingList.name}`;
    this.emailMessage = `Dear ${this.supplier.name},

Please review the attached buying list and confirm the order details. The total order value is ${CurrencyHelper.formatCurrency(totalValue)}.

Best regards,
${merchantName}`;
    this.attachmentFormat = 'pdf';
    this.emailRecipients = this.supplier.contactEmail ? [this.supplier.contactEmail] : ['customer.service@assisty.ai'];
    
    // Show email dialog
    this.showEmailDialog = true;
  }

  private async confirmSendEmail() {
    if (!this.buyingList || !this.supplier || !this.isEmailFormValid) return;
    
    this.sending = true;
    try {
      await this.buyingList.sendEmail({
        emailSubject: this.emailSubject,
        customMessage: this.emailMessage,
        attachmentFormat: this.attachmentFormat,
        recipients: this.emailRecipients
      });
      
      this.showEmailDialog = false;
      EventBus.$emit("show-snackbar", { message: 'Buying list sent successfully', color: "success" });
    } catch (error) {
      EventBus.$emit("show-snackbar", { message: `Failed to send buying list`, color: "error" });
    } finally {
      this.sending = false;
    }
  }

  get locationName(): string {
    if (!this.buyingList?.locationId) return 'All Locations';
    return this.locations.find(l => l.id === this.buyingList?.locationId)?.name || 'Unknown Location';
  }

  private get canManageBuyingLists(): boolean {
    const validateValues = [
      {
        code: 'buying_list',
        value: 'unlocked'
      }
    ];
    const validateResult = this.store.validateRule(validateValues);
    return validateResult.isValidate;
  }

  private handlePOConverted(purchaseOrder: any): void {
    // Refresh buying list after conversion
    this.fetchData();
  }

  private handleSuccessEvent(msg: string) {
    EventBus.$emit("show-snackbar", { message: msg, color: "success" });
  }

  // New method to recalculate all item totals
  private recalculateItemTotals() {
    if (!this.buyingList || !this.buyingList.items) return;
    this.buyingList.items.forEach(item => {
      const quantity = Number(item.quantity || 0);
      const unitPrice = Number(item.unitPrice || 0);
      item.totalPrice = quantity * unitPrice;
    });
  }

  // New method to recalculate total value
  private recalculateTotalValue() {
    if (!this.buyingList || !this.buyingList.items) return;

    this.buyingList.totalValue = this.buyingList.items.reduce((sum, item) => {
      // Use the already calculated item.totalPrice if available, otherwise calculate
      const itemTotal = Number(item.totalPrice || (Number(item.quantity || 0) * Number(item.unitPrice || 0)));
      return sum + itemTotal;
    }, 0);
  }
}
